import { useMemo } from 'react';
import { toast } from 'sonner';
import { X, Printer, FileDown, Loader2, AlertCircle, Handshake } from 'lucide-react';
import { useSettings, useReferralPayout } from '../../services/httpServices/queries';
import { useAppSelector } from '../../redux/store/hooks';
import { referralService } from '../../services/httpServices/referralService';
import { buildReferralPayoutReceiptHtml } from '../../utils/referralPayoutReceipt';
import { openPrintWindow } from '../../utils/printWindow';
import { printPdfBlobUrl } from '../../utils/printDocument';

const CURRENCY = '৳';

function money(value: number | undefined | null): string {
  return `${CURRENCY}${(Number(value) || 0).toLocaleString('en-US', {
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  })}`;
}

/**
 * Referral payout receipt.
 *
 * Renders the modern, printable payout receipt for a settled referral partner.
 * The on-screen preview is the exact document the Print button produces (the
 * receipt HTML is shared), and the official two-copy voucher PDF is offered as
 * a secondary download for the paper trail.
 */
export function ReferralPayoutReceiptModal({
  payoutId,
  onClose,
}: {
  payoutId: string;
  onClose: () => void;
}) {
  const { user } = useAppSelector((state) => state.auth);
  const { data, isLoading, error } = useReferralPayout(payoutId);
  const { data: settingsData } = useSettings();

  const voucher = data?.data;

  const hospital = useMemo(() => {
    const find = (key: string) =>
      settingsData?.data?.find((s: any) => s.key === key)?.value?.trim() || '';
    return {
      name: find('hospital_name_en') || find('hospital_name') || 'Hospital',
      address: find('hospital_address'),
      email: find('hospital_email'),
      website: find('hospital_website'),
      hotline: find('hospital_hotline'),
      logo: find('hospital_logo'),
      certified: find('hospital_certified'),
      signature: find('hospital_signature'),
      seal: find('hospital_seal'),
      signatoryName: find('hospital_signatory_name'),
    };
  }, [settingsData]);

  const html = useMemo(() => {
    if (!voucher) return '';
    const referral = voucher.referral || undefined;
    const preparedBy = voucher.paidBy
      ? `${voucher.paidBy.firstName || ''} ${voucher.paidBy.lastName || ''}`.trim()
      : '';

    // One line per receipt — the partner is paid on the receipt, not per test.
    const groups = new Map<
      string,
      {
        source: string;
        date: string;
        reference: string;
        sharePercent?: number;
        payeeShare: number;
      }
    >();
    const order: string[] = [];
    for (const dist of voucher.distributions || []) {
      const isConsultation = dist.sourceType === 'CONSULTATION';
      const isAdmission = dist.sourceType === 'ADMISSION';
      const receiptNo = dist.receiptItem?.receipt?.receiptNumber;
      const when = isConsultation
        ? dist.appointment?.date
        : isAdmission
          ? dist.admission?.admittedAt
          : dist.receiptItem?.receipt?.createdAt;
      const key = isConsultation
        ? `appt:${dist.id}`
        : isAdmission
          ? `admission:${dist.admission?.id || dist.id}`
          : `receipt:${receiptNo || dist.id}`;
      const pct = Number(dist.doctorPercentage) || 0;
      const share = Number(dist.doctorShare) || 0;

      const existing = groups.get(key);
      if (existing) {
        existing.payeeShare += share;
        if (existing.sharePercent !== pct) existing.sharePercent = undefined;
        continue;
      }
      order.push(key);
      groups.set(key, {
        source: isConsultation
          ? 'Consultation Fee'
          : isAdmission
            ? 'IPD Referral'
            : 'Diagnostic Referral',
        date: when
          ? new Date(when).toLocaleDateString('en-GB', {
              day: '2-digit',
              month: '2-digit',
              year: 'numeric',
            })
          : '—',
        reference: isConsultation
          ? '—'
          : isAdmission
            ? 'IPD admission'
            : receiptNo || '—',
        // A flat IPD commission has no meaningful percentage of the bill.
        sharePercent: isAdmission ? undefined : pct,
        payeeShare: share,
      });
    }
    const lines = order.map((k) => groups.get(k)!);

    return buildReferralPayoutReceiptHtml({
      data: {
        paymentNumber: voucher.paymentNumber,
        status: voucher.status,
        payeeName: voucher.payeeNameSnapshot || referral?.name || 'Referral Partner',
        payeeCode: referral?.code,
        organization: referral?.organization,
        designation: referral?.designation,
        mobileNumber: referral?.mobileNumber,
        bankName: referral?.bankName,
        bankAccountNumber: referral?.bankAccountNumber,
        method: voucher.method,
        paidAt: voucher.paidAt,
        distributionCount: voucher.distributionCount,
        preparedBy,
        notes: voucher.notes,
        isOpening: voucher.isOpening,
        externalReference: voucher.externalReference,
        amount: voucher.amount,
        lines,
      },
      hospital,
      printedBy: user?.name,
    });
  }, [voucher, hospital, user?.name]);

  const handlePrint = () => {
    if (!html) return;
    if (!openPrintWindow(html)) {
      toast.error('Please allow pop-ups to print the receipt');
    }
  };

  const handlePdf = async () => {
    try {
      const url = await referralService.printPayout(payoutId);
      await printPdfBlobUrl(url, { onError: (m) => toast.error(m) });
    } catch (err: any) {
      toast.error(err?.message || 'Failed to generate voucher PDF');
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4">
      <div className="bg-surface rounded-xl border border-border w-full max-w-3xl max-h-[92vh] flex flex-col shadow-xl overflow-hidden">
        {/* Header */}
        <div className="flex items-start gap-3 p-5 border-b border-border">
          <div className="p-2 rounded-lg bg-primary/10 text-primary shrink-0">
            <Handshake size={18} />
          </div>
          <div className="flex-1 min-w-0">
            <h3 className="text-base font-semibold text-text">Referral payout receipt</h3>
            <p className="text-sm text-text-muted truncate">
              {voucher?.paymentNumber || 'Voucher'}
              {voucher?.payeeNameSnapshot ? ` · ${voucher.payeeNameSnapshot}` : ''}
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="p-1.5 rounded-lg text-text-muted hover:text-text hover:bg-background transition-colors"
            aria-label="Close"
          >
            <X size={18} />
          </button>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto bg-background/40">
          {isLoading ? (
            <div className="flex items-center justify-center h-72 text-text-muted">
              <Loader2 size={20} className="animate-spin mr-2" />
              Loading receipt…
            </div>
          ) : error || !voucher ? (
            <div className="flex items-center gap-2 m-5 p-4 bg-danger/10 border border-danger/20 rounded-lg text-sm text-danger">
              <AlertCircle size={16} />
              <span>Failed to load this payout receipt.</span>
            </div>
          ) : (
            <iframe
              title="Referral payout receipt"
              srcDoc={html}
              className="w-full h-[60vh] bg-white border-0"
            />
          )}
        </div>

        {/* Footer */}
        <div className="flex flex-wrap items-center justify-between gap-3 p-4 border-t border-border">
          <div className="text-sm text-text-muted">
            Amount paid: <span className="font-semibold text-text">{money(voucher?.amount)}</span>
          </div>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-sm text-text-muted hover:text-text transition-colors"
            >
              Close
            </button>
            <button
              type="button"
              onClick={handlePdf}
              disabled={!voucher}
              className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg border border-border text-text text-sm font-medium hover:bg-background transition-colors disabled:opacity-40"
            >
              <FileDown size={15} />
              Official PDF
            </button>
            <button
              type="button"
              onClick={handlePrint}
              disabled={!voucher}
              className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg bg-primary text-white text-sm font-medium hover:bg-primary-dark transition-colors disabled:opacity-40"
            >
              <Printer size={15} />
              Print receipt
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
